import React, { useState, useEffect, useRef } from 'react';
import { Phone, Mail, MapPin, CheckCircle, Plus, Search, Users, UserCheck, Edit2, Trash2, ChevronDown, RotateCcw, Briefcase, Calendar } from 'lucide-react';
import { leadsApi, serviceTypesApi } from '../services/api';
import { formatPhoneNumber, formatCurrency, getInitials } from '../utils/helpers';
import { useBusinessContext } from '../contexts/BusinessContext';
import Loading from '../components/Loading';
import Modal from '../components/Modal';
import ConfirmModal from '../components/ConfirmModal';
import MapLink from '../components/MapLink';

// Extracted components
import LeadCard from '../components/leads/LeadCard';
import LeadDetailDrawer from '../components/leads/LeadDetailDrawer';
import LeadFormModal from '../components/leads/LeadFormModal';
import LostReasonModal from '../components/leads/LostReasonModal';
import FollowUpChips from '../components/leads/FollowUpChips';

const Leads = () => {
  const { user } = useBusinessContext();
  const isOwner = user?.role === 'Owner';

  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  // Default to 'active' which excludes Converted leads
  const [filterStatus, setFilterStatus] = useState('active');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  
  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingLead, setEditingLead] = useState(null);

  // Save status for feedback
    const [saveStatus, setSaveStatus] = useState(null);

  // Blocks double-click submits (ref is synchronous, state alone is not)
  const submittingRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Lost-reason prompt (shown when a lead is marked Lost)
  const [lostPrompt, setLostPrompt] = useState({ isOpen: false, leadId: null });
  
  // Confirm modal state
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    variant: 'danger',
    confirmText: 'Confirm',
  });

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    serviceInterest: '',
    message: '',
    estimatedAmount: '',
    isVerified: false,
    isDiscountEligible: false
  });

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    let filtered = leads;
    
    // 'active' = all except Converted (default view)
    if (filterStatus === 'active') {
      filtered = filtered.filter(lead => lead.status !== 'Converted' && lead.status !== 'Lost');
    } else if (filterStatus !== 'all') {
      filtered = filtered.filter(lead => lead.status === filterStatus);
    }
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(lead =>
        lead.name.toLowerCase().includes(term) ||
        lead.phone?.includes(term) ||
        lead.email?.toLowerCase().includes(term) ||
        lead.address?.toLowerCase().includes(term) ||
        lead.city?.toLowerCase().includes(term) ||
        lead.serviceInterest?.toLowerCase().includes(term)
      );
    }
    
    setFilteredLeads(filtered);
  }, [filterStatus, searchTerm, leads]);

    // Open a lead straight from a text-message link (?lead=123)
  useEffect(() => {
    if (loading || leads.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const leadParam = params.get('lead');
    if (!leadParam) return;

    const target = leads.find(l => String(l.id) === leadParam);
    if (target) setSelectedLead(target);

    // Clean the URL so closing the lead or refreshing doesn't reopen it
    params.delete('lead');
    const qs = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
  }, [loading, leads]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [leadsResponse, serviceTypesResponse] = await Promise.all([
        leadsApi.getAll(),
        serviceTypesApi.getAll()
      ]);
      setLeads(leadsResponse.data);
      setFilteredLeads(leadsResponse.data);
      setServiceTypes(serviceTypesResponse.data);
    } catch (error) {
      console.error('Error loading data:', error);
      showError('Failed to load leads. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // HELPER FUNCTIONS
  // ============================================

  const showError = (message) => {
    setConfirmModal({
      isOpen: true,
      title: 'Error',
      message,
      variant: 'danger',
      confirmText: 'OK',
      onConfirm: () => setConfirmModal(prev => ({ ...prev, isOpen: false })),
    });
  };

  const getLeadMissingFields = (lead) => {
    const missing = [];
    if (!lead.address || lead.address.trim().length < 5) missing.push('Street Address');
    if (!lead.city || lead.city.trim().length < 2) missing.push('City');
    if (!lead.state) missing.push('State');
    if (!lead.zipCode || lead.zipCode.trim().length < 5) missing.push('Zip Code');
    return missing;
  };

  const hasCompleteAddress = (lead) => getLeadMissingFields(lead).length === 0;

  const getStatusColor = (status) => {
    const colors = {
      'New': 'bg-blue-100 text-blue-700',
      'Contacted': 'bg-yellow-100 text-yellow-700',
      'Quoted': 'bg-purple-100 text-purple-700',
      'Converted': 'bg-green-100 text-green-700',
      'Lost': 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  // ---- Follow-up helpers (dates are plain YYYY-MM-DD, compared in local time) ----
  const getDateKey = (daysFromNow = 0) => {
    const d = new Date();
    d.setDate(d.getDate() + daysFromNow);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const getFollowUpInfo = (lead) => {
    if (!lead.followUpDate || !['New', 'Contacted', 'Quoted'].includes(lead.status)) return null;
    const key = String(lead.followUpDate).slice(0, 10);
    const [y, m, d] = key.split('-').map(Number);
    const pretty = new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    const today = getDateKey(0);
    if (key < today) return { label: `Follow-up overdue · ${pretty}`, isDue: true };
    if (key === today) return { label: 'Follow up today', isDue: true };
    return { label: `Follow up ${pretty}`, isDue: false };
  };

  const getPriorityScore = (lead) => {
    let score = 0;
    if (getFollowUpInfo(lead)?.isDue) score += 10;
    if (lead.isVerified) score += 2;
    if (lead.estimatedAmount && lead.estimatedAmount > 200) score += 2;
    if (lead.isDiscountEligible) score += 1;
    const daysSinceSubmit = Math.floor((new Date() - new Date(lead.createdAt)) / (1000 * 60 * 60 * 24));
    if (daysSinceSubmit <= 1) score += 3;
    else if (daysSinceSubmit <= 3) score += 2;
    else if (daysSinceSubmit <= 7) score += 1;
    return score;
  };

  const getDaysAgo = (dateString) => {
    if (!dateString) return 'Unknown';
    
    let date = new Date(dateString);
    if (!dateString.endsWith('Z') && !dateString.includes('+') && !dateString.includes('-', 10)) {
      date = new Date(dateString + 'Z');
    }
    
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffMs < 0) return 'Just now';
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
    
    return date.toLocaleDateString();
  };

  const formatFullAddress = (lead) => {
    const parts = [];
    if (lead.address) parts.push(lead.address);
    
    const cityStateZip = [];
    if (lead.city) cityStateZip.push(lead.city);
    if (lead.state) cityStateZip.push(lead.state);
    if (lead.zipCode) cityStateZip.push(lead.zipCode);
    
    if (cityStateZip.length > 0) {
      parts.push(cityStateZip.join(', '));
    }
    
    return parts.join(', ');
  };

  const resetFormData = () => {
    setFormData({
      name: '',
      phone: '',
      email: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      serviceInterest: '',
      message: '',
      estimatedAmount: '',
      isVerified: false,
      isDiscountEligible: false
    });
  };

  // ============================================
  // FORM HANDLERS
  // ============================================

  const handleCloseModal = () => {
    setShowAddModal(false);
    resetFormData();
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingLead(null);
    resetFormData();
  };

  const validateFormData = () => {
    const missingFields = [];
    if (!formData.address || formData.address.trim().length < 5) missingFields.push('Street Address');
    if (!formData.city || formData.city.trim().length < 2) missingFields.push('City');
    if (!formData.state) missingFields.push('State');
    if (!formData.zipCode || formData.zipCode.trim().length < 5) missingFields.push('Zip Code');
    return missingFields;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const missingFields = validateFormData();
    if (missingFields.length > 0) {
      setConfirmModal({
        isOpen: true,
        title: 'Missing Required Fields',
        message: `Please complete the following fields: ${missingFields.join(', ')}. Full address is required for scheduling jobs.`,
        variant: 'danger',
        confirmText: 'OK',
        onConfirm: () => setConfirmModal(prev => ({ ...prev, isOpen: false })),
      });
      return;
    }
    
    if (submittingRef.current) return;
    submittingRef.current = true;
    setIsSubmitting(true);

    try {
      const leadData = {
        name: formData.name,
        phone: formData.phone,
        email: formData.email || null,
        address: formData.address.trim(),
        city: formData.city.trim(),
        state: formData.state,
        zipCode: formData.zipCode.trim(),
        serviceInterest: formData.serviceInterest || null,
        message: formData.message || null,
        // Only include financial data if owner
        estimatedAmount: isOwner && formData.estimatedAmount ? parseFloat(formData.estimatedAmount) : null,
        isVerified: formData.isVerified,
        isDiscountEligible: isOwner ? formData.isDiscountEligible : false,
        source: 'Manual Entry',
        status: 'New'
      };

      await leadsApi.create(leadData);
      handleCloseModal();
      await loadData();
    } catch (error) {
      console.error('Error adding lead:', error);
      showError('Failed to add lead. Please try again.');
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleUpdateLead = async (e) => {
    e.preventDefault();
    
    if (!editingLead) return;

    const missingFields = [];
    if (!formData.name || formData.name.trim().length < 2) missingFields.push('Name');
    if (!formData.phone || formData.phone.trim().length < 10) missingFields.push('Phone');
    missingFields.push(...validateFormData());

    if (missingFields.length > 0) {
      setConfirmModal({
        isOpen: true,
        title: 'Missing Required Fields',
        message: `Please complete the following fields: ${missingFields.join(', ')}.`,
        variant: 'danger',
        confirmText: 'OK',
        onConfirm: () => setConfirmModal(prev => ({ ...prev, isOpen: false })),
      });
      return;
    }
    
    if (submittingRef.current) return;
    submittingRef.current = true;
    setIsSubmitting(true);

    try {
      const leadData = {
        ...editingLead,
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        email: formData.email || null,
        address: formData.address.trim(),
        city: formData.city.trim(),
        state: formData.state,
        zipCode: formData.zipCode.trim(),
        serviceInterest: formData.serviceInterest || null,
        message: formData.message || null,
        // Only update financial data if owner
        estimatedAmount: isOwner && formData.estimatedAmount ? parseFloat(formData.estimatedAmount) : editingLead.estimatedAmount,
        isVerified: formData.isVerified,
        isDiscountEligible: isOwner ? formData.isDiscountEligible : editingLead.isDiscountEligible,
        updatedAt: new Date().toISOString()
      };

      await leadsApi.update(editingLead.id, leadData);
      handleCloseEditModal();
      await loadData();
    } catch (error) {
      console.error('Error updating lead:', error);
      showError('Failed to update lead. Please try again.');
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  // ============================================
  // ACTION HANDLERS
  // ============================================

  const handleCardClick = (lead) => {
    setSelectedLead(lead);
  };

  const handleEditLead = (lead) => {
    setEditingLead(lead);
    setFormData({
      name: lead.name || '',
      phone: lead.phone || '',
      email: lead.email || '',
      address: lead.address || '',
      city: lead.city || '',
      state: lead.state || '',
      zipCode: lead.zipCode || '',
      serviceInterest: lead.serviceInterest || '',
      message: lead.message || '',
      estimatedAmount: lead.estimatedAmount?.toString() || '',
      isVerified: lead.isVerified || false,
      isDiscountEligible: lead.isDiscountEligible || false
    });
    setShowEditModal(true);
    setSelectedLead(null);
  };

  // Entry point for every status change (card, drawer, desktop modal).
  // Marking a lead Lost asks for a reason first; anything else saves right away.
  const handleUpdateStatus = (leadId, newStatus) => {
    if (newStatus === 'Lost') {
      setLostPrompt({ isOpen: true, leadId });
      return;
    }
    saveLeadStatus(leadId, newStatus, null);
  };

  const saveLeadStatus = async (leadId, newStatus, lostReason = null) => {
    const lead = leads.find(l => l.id === leadId);
    if (!lead) return;

    // Optimistic update
    const updatedLead = { ...lead, status: newStatus };
    setLeads(prev => prev.map(l => l.id === leadId ? updatedLead : l));
    if (selectedLead?.id === leadId) {
      setSelectedLead(updatedLead);
    }

    setSaveStatus('saving');

    try {
      const response = await leadsApi.updateStatus(leadId, newStatus, lostReason);

      // Sync with what the server actually saved (status, reason, timestamp)
      const saved = response.data;
      const syncLead = (l) => l.id === leadId
        ? { ...l, status: saved.status, lostReason: saved.lostReason, lostAt: saved.lostAt }
        : l;
      setLeads(prev => prev.map(syncLead));
      setSelectedLead(prev => (prev && prev.id === leadId ? syncLead(prev) : prev));
      
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(null), 2000);
    } catch (error) {
      console.error('Error updating lead status:', error);
      // Revert on error
      setLeads(prev => prev.map(l => l.id === leadId ? lead : l));
      if (selectedLead?.id === leadId) {
        setSelectedLead(lead);
      }
      setSaveStatus(null);
      showError('Failed to update lead status.');
    }
  };

  // Set (daysFromNow = 1, 3, 7...) or clear (null) a lead's follow-up reminder
  const handleSetFollowUp = async (leadId, daysFromNow) => {
    const lead = leads.find(l => l.id === leadId);
    if (!lead) return;

    const newDate = daysFromNow === null ? null : getDateKey(daysFromNow);
    const previous = lead.followUpDate ?? null;

    const applyDate = (value) => {
      const sync = (l) => (l.id === leadId ? { ...l, followUpDate: value } : l);
      setLeads(prev => prev.map(sync));
      setSelectedLead(prev => (prev && prev.id === leadId ? sync(prev) : prev));
    };

    applyDate(newDate); // optimistic

    try {
      const response = await leadsApi.setFollowUpDate(leadId, newDate);
      applyDate(response.data?.followUpDate ?? null);
    } catch (error) {
      console.error('Error saving follow-up date:', error);
      applyDate(previous);
      showError('Failed to save the follow-up date.');
    }
  };

  const handleConvertToCustomer = (lead) => {
    const missingFields = getLeadMissingFields(lead);
    const isReturningCustomer = !!lead.matchedCustomerId;
    
    if (missingFields.length > 0) {
      setConfirmModal({
        isOpen: true,
        title: 'Incomplete Address',
        message: `"${lead.name}" is missing: ${missingFields.join(', ')}. Please edit the lead to add this information before ${isReturningCustomer ? 'creating a job' : 'converting to a customer'}.`,
        variant: 'warning',
        confirmText: 'Edit Lead',
        cancelText: 'Cancel',
        onConfirm: () => {
          setConfirmModal(prev => ({ ...prev, isOpen: false }));
          handleEditLead(lead);
        },
      });
      return;
    }

    // Different confirmation message for returning vs new customers
    const confirmConfig = isReturningCustomer
      ? {
          title: 'Create Job for Returning Customer',
          message: `${lead.name} is already in your customer list. This will create a new job for them${lead.estimatedAmount ? ` with a quote of ${formatCurrency(lead.estimatedAmount)}` : ''}. No duplicate customer will be created.`,
          confirmText: 'Create Job',
          variant: 'info',
        }
      : {
          title: 'Convert to Customer',
          message: `Convert "${lead.name}" to a customer? This will create a new customer record and a job${lead.estimatedAmount ? ` with a quote of ${formatCurrency(lead.estimatedAmount)}` : ''}.`,
          confirmText: 'Convert',
          variant: 'info',
        };

    setConfirmModal({
      isOpen: true,
      ...confirmConfig,
      onConfirm: async () => {
        if (submittingRef.current) return;
        submittingRef.current = true;
        setIsSubmitting(true);

        try {
          const response = await leadsApi.convertToCustomer(lead.id);
          await loadData();
          setSelectedLead(null);
          setConfirmModal(prev => ({ ...prev, isOpen: false }));
          
          // Show success message
          const successMessage = response.data?.isReturningCustomer
            ? `Job created for ${response.data.customerName}!`
            : `Customer and job created for ${response.data?.customerName || lead.name}!`;
          
          setConfirmModal({
            isOpen: true,
            title: '✓ Success',
            message: successMessage,
            variant: 'success',
            confirmText: 'OK',
            onConfirm: () => setConfirmModal(prev => ({ ...prev, isOpen: false })),
          });
        } catch (error) {
          console.error('Error converting lead:', error);
          showError(error.response?.data?.error || 'Failed to convert lead.');
        } finally {
          submittingRef.current = false;
          setIsSubmitting(false);
        }
      },
    });
  };

  const handleDeleteLead = (lead) => {
    if (!isOwner) return; // Safety check

    setConfirmModal({
      isOpen: true,
      title: 'Delete Lead',
      message: lead.status === 'Converted'
        ? `Delete the lead record for "${lead.name}"? The customer and job it created are not deleted.`
        : `Are you sure you want to delete "${lead.name}"? This action cannot be undone.`,
      variant: 'danger',
      confirmText: 'Delete',
      onConfirm: async () => {
        if (submittingRef.current) return;
        submittingRef.current = true;
        setIsSubmitting(true);

        try {
          await leadsApi.delete(lead.id);
          await loadData();
          setSelectedLead(null);
          setConfirmModal(prev => ({ ...prev, isOpen: false }));
        } catch (error) {
          console.error('Error deleting lead:', error);
          showError('Failed to delete lead.');
        } finally {
          submittingRef.current = false;
          setIsSubmitting(false);
        }
      },
    });
  };

  // ============================================
  // RENDER
  // ============================================

  if (loading) return <Loading fullScreen />;

  const stats = {
    total: leads.length,
    new: leads.filter(l => l.status === 'New').length,
    contacted: leads.filter(l => l.status === 'Contacted').length,
    quoted: leads.filter(l => l.status === 'Quoted').length,
    converted: leads.filter(l => l.status === 'Converted').length,
    lost: leads.filter(l => l.status === 'Lost').length
  };

  // Leads whose follow-up date is today or earlier
  const dueFollowUps = leads.filter(l => getFollowUpInfo(l)?.isDue).length;

  // Calculate active count (excludes Converted)
  const activeCount = leads.filter(l => l.status !== 'Converted' && l.status !== 'Lost').length;

  // Team members: Active leads only, no Converted option
  // Owner: Can see Converted for analytics
  const statusFilters = isOwner 
    ? [
        { value: 'active', label: 'Active', count: activeCount },
        { value: 'New', label: 'New', count: stats.new },
        { value: 'Contacted', label: 'Contacted', count: stats.contacted },
        { value: 'Quoted', label: 'Quoted', count: stats.quoted },
        { value: 'Lost', label: 'Lost', count: stats.lost },
        { value: 'Converted', label: 'Converted', count: stats.converted },
        { value: 'all', label: 'All', count: stats.total },
      ]
    : [
        { value: 'active', label: 'Active', count: activeCount },
        { value: 'New', label: 'New', count: stats.new },
        { value: 'Contacted', label: 'Contacted', count: stats.contacted },
        { value: 'Quoted', label: 'Quoted', count: stats.quoted },
        { value: 'Lost', label: 'Lost', count: stats.lost },
      ];

  return (
    <div className="p-4 lg:p-8 pb-20 lg:pb-8">
      {/* Header with Compact Stats */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Leads</h1>
          <div className="flex items-center gap-3 text-sm text-gray-600 mt-1">
            <span className="flex items-center gap-1">
              <Users size={14} />
              {stats.total} total
            </span>
            <span className="text-blue-600 font-medium">{stats.new} new</span>
            <span className="text-green-600 font-medium">{stats.converted} converted</span>
            {dueFollowUps > 0 && (
              <span className="text-amber-600 font-medium">
                {dueFollowUps} follow-up{dueFollowUps !== 1 ? 's' : ''} due
              </span>
            )}
            {stats.lost > 0 && (
              <span className="text-gray-500 font-medium">{stats.lost} lost</span>
            )}
          </div>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="btn btn-primary flex items-center justify-center gap-2 w-full sm:w-auto"
        >
          <Plus size={18} />
          Add Lead
        </button>
      </div>

      {/* Pro Tip - Lead Workflow */}
      {stats.new > 0 && (
        <div className="mb-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="text-2xl">💡</div>
            <div className="flex-1">
              {/* Desktop: Full version */}
              <p className="hidden sm:block font-semibold text-blue-900 mb-1">Pro Tip: Lead Workflow</p>
              <p className="hidden sm:block text-sm text-blue-700">
                <span className="font-medium">New</span> → Contact them → 
                <span className="font-medium"> Contacted</span> → Send quote → 
                <span className="font-medium"> Quoted</span> → Win the job → 
                <span className="font-medium"> Convert to Customer</span>
              </p>
              {/* Mobile: Shortened version */}
              <p className="sm:hidden text-sm text-blue-700 font-medium">
                New → Contacted → Quoted → Convert
              </p>
              <p className="text-xs text-blue-600 mt-1">
                Status changes save automatically. Click a lead to call, email, or update their status.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search leads..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input pl-9 pr-9 py-2.5 text-sm"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-lg"
            >
              ×
            </button>
          )}
        </div>
      </div>

      {/* Status Filters */}
      <div className="mb-4 overflow-x-auto scrollbar-hide">
        <div className="flex gap-2 pb-1">
          {statusFilters.map((filter) => (
            <button
              key={filter.value}
              onClick={() => setFilterStatus(filter.value)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                filterStatus === filter.value
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {filter.label}
              {filter.count > 0 && (
                <span className={`ml-1.5 ${filterStatus === filter.value ? 'text-primary-200' : 'text-gray-400'}`}>
                  {filter.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Lost reasons tally (Lost tab only) */}
      {filterStatus === 'Lost' && stats.lost > 0 && (() => {
        const counts = {};
        leads.filter(l => l.status === 'Lost').forEach(l => {
          const key = l.lostReason || 'No reason given';
          counts[key] = (counts[key] || 0) + 1;
        });
        return (
          <div className="mb-4 flex flex-wrap gap-2">
            {Object.entries(counts).sort((a, b) => b[1] - a[1]).map(([reason, count]) => (
              <span key={reason} className="text-xs bg-gray-100 text-gray-700 px-2.5 py-1 rounded-full">
                {reason} <span className="font-semibold">{count}</span>
              </span>
            ))}
          </div>
        );
      })()}

      {/* Results Count */}
      {searchTerm && (
        <p className="text-sm text-gray-500 mb-3">
          Found {filteredLeads.length} lead{filteredLeads.length !== 1 ? 's' : ''}
        </p>
      )}

      {/* Leads List */}
      {filteredLeads.length === 0 ? (
        <div className="card text-center py-12">
          <Users className="mx-auto h-12 w-12 text-gray-300 mb-4" />
          <p className="text-gray-500 text-lg font-medium mb-2">
            {searchTerm ? 'No leads found' : filterStatus !== 'all' ? `No ${filterStatus.toLowerCase()} leads` : 'No leads yet'}
          </p>
          <p className="text-gray-400 text-sm mb-4">
            {searchTerm ? 'Try a different search' : 'Add your first lead to get started'}
          </p>
          {!searchTerm && filterStatus === 'all' && (
            <button
              onClick={() => setShowAddModal(true)}
              className="btn btn-primary inline-flex items-center gap-2"
            >
              <Plus size={18} />
              Add Lead
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredLeads
            .slice()
            .sort((a, b) => getPriorityScore(b) - getPriorityScore(a))
            .map((lead) => (
              <LeadCard
                key={lead.id}
                lead={lead}
                isOwner={isOwner}
                onCardClick={handleCardClick}
                onUpdateStatus={handleUpdateStatus}
                onEdit={handleEditLead}
                onConvert={handleConvertToCustomer}
                onDelete={handleDeleteLead}
                hasCompleteAddress={hasCompleteAddress}
                getDaysAgo={getDaysAgo}
                getFollowUpInfo={getFollowUpInfo}
                getStatusColor={getStatusColor}
              />
            ))}
        </div>
      )}

      {/* Mobile Lead Detail Drawer */}
      {selectedLead && isMobile && (
        <LeadDetailDrawer
          lead={selectedLead}
          isOwner={isOwner}
          onClose={() => setSelectedLead(null)}
          onUpdateStatus={handleUpdateStatus}
          onEdit={handleEditLead}
          onConvert={handleConvertToCustomer}
          onDelete={handleDeleteLead}
          saveStatus={saveStatus}
          onSetFollowUp={handleSetFollowUp}
          getFollowUpInfo={getFollowUpInfo}
          hasCompleteAddress={hasCompleteAddress}
          getLeadMissingFields={getLeadMissingFields}
          formatFullAddress={formatFullAddress}
          getStatusColor={getStatusColor}
        />
      )}

      {/* Desktop Lead Detail Modal */}
      {selectedLead && !isMobile && (
        <Modal
          isOpen={!!selectedLead}
          onClose={() => setSelectedLead(null)}
          title={selectedLead.name}
        >
          {/* Check if returning customer */}
          {(() => {
            const isReturningCustomer = !!selectedLead.matchedCustomerId;
            
            return (
              <div className="space-y-4">
                {/* Status & Customer Type Badges */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-sm px-3 py-1 rounded-full font-medium ${getStatusColor(selectedLead.status)}`}>
                    {selectedLead.status}
                  </span>
                  {selectedLead.status === 'Lost' && selectedLead.lostReason && (
                    <span className="text-sm text-gray-500">Reason: {selectedLead.lostReason}</span>
                  )}
                  
                  {/* RETURNING CUSTOMER BADGE */}
                  {isReturningCustomer && (
                    <span className="text-sm bg-blue-100 text-blue-700 px-3 py-1 rounded-full font-medium flex items-center gap-1">
                      <RotateCcw size={14} />
                      Returning Customer
                    </span>
                  )}
                  
                  {selectedLead.isVerified && (
                    <span className="text-sm text-green-600 flex items-center gap-1">
                      <CheckCircle size={14} /> Verified
                    </span>
                  )}
                  
                  {/* Instant Quote badge */}
                  {selectedLead.hasInstantQuote && (
                    <span className="text-sm bg-purple-100 text-purple-700 px-2 py-0.5 rounded flex items-center gap-1">
                      ✨ Instant Quote
                    </span>
                  )}
                  
                  {/* OWNER ONLY: Discount badge */}
                  {isOwner && selectedLead.isDiscountEligible && (
                    <span className="text-sm bg-orange-100 text-orange-700 px-2 py-0.5 rounded">
                      🎖️ Discount Eligible
                    </span>
                  )}
                </div>

                {/* RETURNING CUSTOMER CALLOUT */}
                {isReturningCustomer && selectedLead.status !== 'Converted' && selectedLead.status !== 'Lost' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <RotateCcw size={20} className="text-blue-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-blue-900">Returning Customer!</p>
                        <p className="text-sm text-blue-700 mt-1">
                          {selectedLead.name} is already in your customer list. Click "Create Job" to add a new job for them - no duplicate customer will be created.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Contact Info */}
                <div className="space-y-2">
                  {selectedLead.phone && (
                    <div className="flex items-center gap-2">
                      <Phone size={16} className="text-gray-400" />
                      <a href={`tel:${selectedLead.phone}`} className="text-primary-600 hover:underline">
                        {formatPhoneNumber(selectedLead.phone)}
                      </a>
                    </div>
                  )}
                  {selectedLead.email && (
                    <div className="flex items-center gap-2">
                      <Mail size={16} className="text-gray-400" />
                      <a href={`mailto:${selectedLead.email}`} className="text-primary-600 hover:underline">
                        {selectedLead.email}
                      </a>
                    </div>
                  )}
                  {(selectedLead.address || selectedLead.city) && (
                    <MapLink 
                      address={selectedLead.address}
                      city={selectedLead.city}
                      state={selectedLead.state}
                      zipCode={selectedLead.zipCode}
                      iconSize={16}
                      className="text-gray-700"
                    />
                  )}
                </div>

                {/* Service & Amount */}
                {(selectedLead.serviceInterest || (isOwner && selectedLead.estimatedAmount)) && (
                  <div className="grid grid-cols-2 gap-3">
                    {selectedLead.serviceInterest && (
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs text-gray-500 mb-1">Service Interest</p>
                        <p className="font-medium text-gray-900">{selectedLead.serviceInterest}</p>
                      </div>
                    )}
                    {/* OWNER ONLY: Estimated Value */}
                    {isOwner && selectedLead.estimatedAmount && (
                      <div className="bg-green-50 rounded-lg p-3">
                        <p className="text-xs text-gray-500 mb-1">
                          {selectedLead.hasInstantQuote ? '✨ Instant Quote' : 'Estimated Value'}
                        </p>
                        <p className="font-semibold text-green-600">{formatCurrency(selectedLead.estimatedAmount)}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* OWNER ONLY: Custom Quote Needed Warning */}
                {isOwner && !selectedLead.hasInstantQuote && !selectedLead.estimatedAmount && selectedLead.status !== 'Converted' && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-amber-800 text-sm font-medium flex items-center gap-2">
                      📝 Custom Quote Needed
                    </p>
                    <p className="text-amber-600 text-xs mt-1">
                      This service requires a custom quote. Click "Edit" to add an estimated amount before creating the job, or create the job now and add pricing later.
                    </p>
                  </div>
                )}

                {/* Preferred Schedule */}
                {selectedLead.preferredSchedule && (
                  <div className="bg-purple-50 rounded-lg p-3 flex items-center gap-3">
                    <Calendar size={18} className="text-purple-600" />
                    <div>
                      <p className="text-xs text-gray-500">Preferred Timing</p>
                      <p className="font-medium text-purple-700">{selectedLead.preferredSchedule}</p>
                    </div>
                  </div>
                )}

                {/* Message */}
                {selectedLead.message && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-1">Customer Message</p>
                    <p className="text-gray-600 bg-gray-50 p-3 rounded-lg whitespace-pre-wrap text-sm">
                      {selectedLead.message}
                    </p>
                  </div>
                )}

                {/* Details Grid */}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-500">Source:</span>
                    <span className="ml-2 font-medium">{selectedLead.source || 'Unknown'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Submitted:</span>
                    <span className="ml-2 font-medium">{new Date(selectedLead.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="pt-4 border-t border-gray-200 space-y-3">
                  {/* Converted leads are read-only */}
                  {selectedLead.status === 'Converted' ? (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                      <CheckCircle size={24} className="inline text-green-600 mb-2" />
                      <p className="text-green-800 font-medium">Converted to Customer</p>
                      <p className="text-sm text-green-600 mt-1">
                        {selectedLead.convertedAt && new Date(selectedLead.convertedAt).toLocaleDateString()}
                      </p>
                      <p className="text-xs text-gray-500 mt-2">
                        This lead is now read-only
                      </p>
                      {isOwner && (
                        <button
                          onClick={() => handleDeleteLead(selectedLead)}
                          className="mt-3 w-full py-2 border-2 border-red-200 text-red-600 font-medium rounded-lg bg-white hover:bg-red-50 transition-colors"
                        >
                          Delete Lead
                        </button>
                      )}
                    </div>
                  ) : (
                    <>
                      {/* Incomplete Address Warning */}
                      {selectedLead.status !== 'Lost' && !hasCompleteAddress(selectedLead) && (
                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                          <p className="text-orange-800 text-sm font-medium flex items-center gap-2">
                            ⚠️ Missing Information
                          </p>
                          <p className="text-orange-600 text-xs mt-1">
                            {getLeadMissingFields(selectedLead).join(', ')} required before {isReturningCustomer ? 'creating job' : 'converting to customer'}.
                          </p>
                        </div>
                      )}

                      {/* Follow-up reminder */}
                      <FollowUpChips
                        lead={selectedLead}
                        onSetFollowUp={handleSetFollowUp}
                        getFollowUpInfo={getFollowUpInfo}
                      />

                      {/* Status Selector - Only for NEW customers (not returning) */}
                      {!isReturningCustomer && (
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <label className="text-xs text-gray-500">Update Status</label>
                            {saveStatus === 'saving' && (
                              <span className="text-xs text-gray-400 flex items-center gap-1">
                                <span className="animate-spin">⏳</span> Saving...
                              </span>
                            )}
                            {saveStatus === 'saved' && (
                              <span className="text-xs text-green-600 flex items-center gap-1">
                                <CheckCircle size={12} /> Saved
                              </span>
                            )}
                          </div>
                          <div className="relative">
                            <select
                              value={selectedLead.status}
                              onChange={(e) => handleUpdateStatus(selectedLead.id, e.target.value)}
                              className="w-full appearance-none bg-white border border-gray-200 rounded-lg pl-3 pr-8 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                            >
                              <option value="New">New</option>
                              <option value="Contacted">Contacted</option>
                              <option value="Quoted">Quoted</option>
                              <option value="Lost">Lost</option>
                            </select>
                            <ChevronDown size={16} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                          </div>
                        </div>
                      )}
                      
                      {/* Returning customers have no status dropdown, so give them a way to mark Lost */}
                      {isReturningCustomer && selectedLead.status !== 'Lost' && (
                        <button
                          onClick={() => handleUpdateStatus(selectedLead.id, 'Lost')}
                          className="w-full py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          Mark as Lost
                        </button>
                      )}

                      {/* Edit & Convert/Create Job Buttons */}
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditLead(selectedLead)}
                          className="btn btn-secondary flex-1 justify-center"
                        >
                          <Edit2 size={16} className="mr-2" />
                          Edit
                        </button>
                        
                        {/* Lost leads: Reopen replaces Convert */}
                        {selectedLead.status === 'Lost' && (
                          <button
                            onClick={() => handleUpdateStatus(selectedLead.id, 'New')}
                            className="btn btn-secondary flex-1 justify-center"
                          >
                            <RotateCcw size={16} className="mr-2" />
                            Reopen
                          </button>
                        )}

                        {/* SMART BUTTON - Different for returning vs new */}
                        <button
                          onClick={() => handleConvertToCustomer(selectedLead)}
                          disabled={!hasCompleteAddress(selectedLead)}
                          className={`flex-1 justify-center ${selectedLead.status === 'Lost' ? 'hidden' : 'flex'} items-center px-4 py-2 rounded-lg font-medium transition-colors ${
                            hasCompleteAddress(selectedLead)
                              ? isReturningCustomer
                                ? 'bg-blue-600 text-white hover:bg-blue-700'   // Returning - blue
                                : 'bg-primary-600 text-white hover:bg-primary-700' // New - green/primary
                              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          }`}
                        >
                          {isReturningCustomer ? (
                            <>
                              <Briefcase size={16} className="mr-2" />
                              Create Job
                            </>
                          ) : (
                            <>
                              <UserCheck size={16} className="mr-2" />
                              Convert
                            </>
                          )}
                        </button>
                      </div>
                      
                      {/* Helper text */}
                      {selectedLead.status !== 'Lost' && hasCompleteAddress(selectedLead) && (
                        <p className="text-xs text-center text-gray-500">
                          {isReturningCustomer 
                            ? "Creates a new job linked to their existing customer profile"
                            : "Creates a new customer and a job"
                          }
                        </p>
                      )}

                      {/* OWNER ONLY: Delete button */}
                      {isOwner && (
                        <button
                          onClick={() => handleDeleteLead(selectedLead)}
                          className="w-full py-2 border-2 border-red-200 text-red-600 font-medium rounded-lg hover:bg-red-50 transition-colors"
                        >
                          Delete Lead
                        </button>
                      )}
                    </>
                  )}

                  <div className="flex gap-2">
                    <a href={`tel:${selectedLead.phone}`} className="btn btn-secondary flex-1 justify-center">
                      <Phone size={16} className="mr-2" />
                      Call
                    </a>
                    {selectedLead.email && (
                      <a href={`mailto:${selectedLead.email}`} className="btn btn-secondary flex-1 justify-center">
                        <Mail size={16} className="mr-2" />
                        Email
                      </a>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}
        </Modal>
      )}

      {/* Lost reason prompt */}
      {lostPrompt.isOpen && (
        <LostReasonModal
          leadName={leads.find(l => l.id === lostPrompt.leadId)?.name || 'this lead'}
          onCancel={() => setLostPrompt({ isOpen: false, leadId: null })}
          onConfirm={(reason) => {
            const id = lostPrompt.leadId;
            setLostPrompt({ isOpen: false, leadId: null });
            saveLeadStatus(id, 'Lost', reason);
          }}
        />
      )}

      {/* Add Lead Modal */}
      <LeadFormModal
        isOpen={showAddModal}
        onClose={handleCloseModal}
        onSubmit={handleSubmit}
        formData={formData}
        setFormData={setFormData}
        serviceTypes={serviceTypes}
        isOwner={isOwner}
        isEditing={false}
        isSubmitting={isSubmitting}
      />

      {/* Edit Lead Modal */}
      <LeadFormModal
        isOpen={showEditModal}
        onClose={handleCloseEditModal}
        onSubmit={handleUpdateLead}
        formData={formData}
        setFormData={setFormData}
        serviceTypes={serviceTypes}
        isOwner={isOwner}
        isEditing={true}
        editingLead={editingLead}
        isSubmitting={isSubmitting}
        hasCompleteAddress={hasCompleteAddress}
      />

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        confirmText={confirmModal.confirmText}
      />
    </div>
  );
};

export default Leads;